// src/pages/SuperadminUsersPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Button, Container, Typography, Stack, Chip, createTheme, ThemeProvider,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Avatar, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Select, FormControl, InputLabel, alpha, Snackbar, Alert,
    CircularProgress, useMediaQuery, useTheme, Divider, FormControlLabel, Switch,
    Checkbox, FormGroup, Grid, InputAdornment, CssBaseline, GlobalStyles, styled
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Add, DeleteOutline, ModeEditOutline, PersonOutline, Apps, Save, UploadFile,
    CloudUpload, Download, Clear, Search, FilterList, History, Close
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import api from '../services/api';
import { API_BASE_URL } from '../config';

// ---------------------------------------------------------------------------
// Configurações
// ---------------------------------------------------------------------------
const allRoles = [
    { value: 'superadmin', label: 'Super Admin', color: 'secondary' },
    { value: 'admin',      label: 'Admin',        color: 'error'     },
    { value: 'editor',     label: 'Editor',        color: 'info'      },
    { value: 'viewer',     label: 'Visualizador',  color: 'default'   },
];

const singularRoles = allRoles.filter(r => ['superadmin', 'editor'].includes(r.value));
const roleConfig    = allRoles.reduce((acc, r) => { acc[r.value] = r; return acc; }, {});

const licenseConfig = {
    pequena: { label: 'Pequena', color: 'primary' },
    media:   { label: 'Média',   color: 'warning' },
    grande:  { label: 'Grande',  color: 'error'   },
};

// ---------------------------------------------------------------------------
// TEMA DARK
// ---------------------------------------------------------------------------
const customTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#315e9a', contrastText: '#ffffff' },
        secondary: { main: '#90caf9' },
        background: { default: '#0f1218', paper: '#1a1d24' },
        divider: 'rgba(0,0,0,0)',
    },
    typography: { button: { textTransform: 'none', fontWeight: 600 } },
    shape: { borderRadius: 12 },
});

const DarkPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'none',
    border: 'none',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    transition: 'all 0.2s ease-in-out',
    borderRadius: 16,
    minWidth: 0,
}));

// ---------------------------------------------------------------------------
// LoginHistoryModal
// ---------------------------------------------------------------------------
const LoginHistoryModal = ({ open, onClose, logs, title, loading }) => (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack sx={{ minWidth: 0 }}>
                <Typography variant="h6" fontWeight="bold">Histórico de Acesso</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>{title}</Typography>
            </Stack>
            <IconButton onClick={onClose}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'transparent' }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : logs.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                    <History sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography>Nenhum registro de acesso encontrado.</Typography>
                </Box>
            ) : (
                <TableContainer sx={{ width: '100%', minWidth: 0 }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead sx={{ bgcolor: alpha('#90caf9', 0.05) }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Data e Hora</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Usuário</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>IP</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Dispositivo/Navegador</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {format(parseISO(log.createdAt), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                                    </TableCell>
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {log.user ? log.user.name : '---'}
                                    </TableCell>
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {log.ipAddress || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Tooltip title={log.userAgent || ''}>
                                            <Typography variant="caption" noWrap display="block">{log.userAgent || 'Desconhecido'}</Typography>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
);

// ---------------------------------------------------------------------------
// CsvImportModal
// ---------------------------------------------------------------------------
const CsvImportModal = ({ open, onClose, onSuccess, setSnackbar, companies, units, allSoftwares }) => {
    const [file, setFile] = useState(null);
    const [userCount, setUserCount] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [password, setPassword] = useState('');
    const [isSingular, setIsSingular] = useState(true);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [selectedSoftwareIds, setSelectedSoftwareIds] = useState([]);

    useEffect(() => {
        if (!open) {
            setFile(null); setUserCount(0); setPassword('');
            setIsSingular(true); setSelectedCompanyId('');
            setSelectedUnitId(''); setSelectedSoftwareIds([]); setUploading(false);
        }
    }, [open]);

    useEffect(() => { setSelectedUnitId(''); }, [selectedCompanyId, isSingular]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            const reader = new FileReader();
            reader.onload = (evt) => {
                const text = evt.target.result;
                const lines = text.split('\n').filter(line => line.trim() !== '');
                setUserCount(lines.length > 1 ? lines.length - 1 : 0);
            };
            reader.readAsText(selectedFile);
        }
    };

    const handleDownloadTemplate = () => {
        const csvContent = "name,email,role,cargo\nJoão Silva,joao@exemplo.com,viewer,Analista";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "modelo_usuarios.csv");
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    const handleToggleSoftware = (id) => {
        setSelectedSoftwareIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('password', password);
        formData.append('isSingular', isSingular);
        if (!isSingular && selectedCompanyId) formData.append('companyId', selectedCompanyId);
        if (!isSingular && selectedUnitId) formData.append('unitId', selectedUnitId);
        formData.append('softwareIds', JSON.stringify(selectedSoftwareIds));
        try {
            await api.post('/users/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSnackbar({ open: true, message: 'Usuários importados com sucesso!', severity: 'success' });
            onSuccess(); onClose();
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Erro ao processar o arquivo CSV.',
                severity: 'error'
            });
        } finally { setUploading(false); }
    };

    const isValid = file && password.length > 0 && (isSingular || (!isSingular && selectedCompanyId !== ''));

    return (
        <Dialog open={open} onClose={() => !uploading && onClose()} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight="bold">Importar Usuários em Massa</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                <Stack spacing={4} sx={{ minWidth: 0 }}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold">1. Selecione o Arquivo CSV</Typography>
                            <Button size="small" variant="text" startIcon={<Download />} onClick={handleDownloadTemplate}>Baixar Modelo</Button>
                        </Stack>

                        {!file ? (
                            <Box sx={{
                                border: '2px dashed', borderColor: 'rgba(255,255,255,0.1)',
                                borderRadius: 2, p: 4, textAlign: 'center', transition: 'all 0.2s',
                                '&:hover': { borderColor: 'primary.main', bgcolor: alpha('#90caf9', 0.04) }
                            }}>
                                <input accept=".csv" style={{ display: 'none' }} id="csv-upload" type="file" onChange={handleFileChange} />
                                <label htmlFor="csv-upload" style={{ cursor: 'pointer', display: 'block' }}>
                                    <CloudUpload fontSize="large" sx={{ color: 'secondary.main', mb: 1 }} />
                                    <Typography variant="subtitle1" fontWeight={500}>Clique para selecionar o arquivo CSV</Typography>
                                </label>
                            </Box>
                        ) : (
                            <Paper variant="outlined" sx={{
                                p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                borderColor: 'primary.main', bgcolor: alpha('#90caf9', 0.06), borderRadius: 2,
                                gap: 2, minWidth: 0
                            }}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                                    <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1, borderRadius: 1, display: 'flex', flexShrink: 0 }}><UploadFile /></Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography fontWeight="bold" noWrap>{file.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {userCount > 0 ? `${userCount} usuário(s) detectado(s)` : 'Nenhum usuário válido encontrado'}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <IconButton onClick={() => setFile(null)} size="small" color="error" sx={{ flexShrink: 0 }}><Clear /></IconButton>
                            </Paper>
                        )}
                    </Box>

                    {file && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>2. Configurações Globais (Aplicado a todos)</Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Senha Padrão Inicial" fullWidth required type="password"
                                        value={password} onChange={(e) => setPassword(e.target.value)}
                                        helperText="Será definida para todos os usuários importados."
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        px: 2, py: 1.5, borderRadius: 2, border: '1px solid',
                                        borderColor: isSingular ? 'primary.main' : 'rgba(255,255,255,0.1)',
                                        bgcolor: isSingular ? alpha('#90caf9', 0.06) : 'transparent',
                                        height: '100%', boxSizing: 'border-box'
                                    }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={500}>Usuários Singulares?</Typography>
                                            <Typography variant="caption" color="text.secondary">Não vincular a uma empresa.</Typography>
                                        </Box>
                                        <FormControlLabel control={<Switch checked={isSingular} onChange={e => setIsSingular(e.target.checked)} color="primary" />} label="" sx={{ m: 0 }} />
                                    </Box>
                                </Grid>

                                {!isSingular && (
                                    <Grid item xs={12}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
                                            <FormControl fullWidth required>
                                                <InputLabel id="csv-company-label">Vincular à Empresa</InputLabel>
                                                <Select labelId="csv-company-label" value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)} label="Vincular à Empresa">
                                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                                    {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                            <FormControl fullWidth disabled={!selectedCompanyId}>
                                                <InputLabel id="csv-unit-label">Vincular à Unidade (Opcional)</InputLabel>
                                                <Select labelId="csv-unit-label" value={selectedUnitId} onChange={e => setSelectedUnitId(e.target.value)} label="Vincular à Unidade (Opcional)">
                                                    <MenuItem value=""><em>Nenhuma</em></MenuItem>
                                                    {units.filter(u => u.companyId === selectedCompanyId).map(u => (
                                                        <MenuItem key={u.id} value={u.id}>{u.name} {u.isMatriz ? '(Matriz)' : ''}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Typography variant="body2" fontWeight="bold" mb={1}>Permissões de Software</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
                                        {allSoftwares.length === 0 ? (
                                            <Typography variant="caption" color="text.secondary">Nenhum software cadastrado.</Typography>
                                        ) : (
                                            <FormGroup row>
                                                {allSoftwares.map(software => (
                                                    <FormControlLabel
                                                        key={software.id}
                                                        sx={{ width: { xs: '100%', sm: '48%' }, mb: 1, mr: 0 }}
                                                        control={
                                                            <Checkbox checked={selectedSoftwareIds.includes(software.id)} onChange={() => handleToggleSoftware(software.id)} color="primary" />
                                                        }
                                                        label={
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography variant="body2">{software.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 200 }}>
                                                                    {software.description}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                ))}
                                            </FormGroup>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </motion.div>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose} disabled={uploading}>Cancelar</Button>
                <Button onClick={handleUpload} variant="contained" disabled={!isValid || uploading || userCount === 0}
                    startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <Save />}>
                    {uploading ? 'Processando...' : `Importar ${userCount} Usuário(s)`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// SingularSoftwareModal
// ---------------------------------------------------------------------------
const SingularSoftwareModal = ({ open, onClose, user, allSoftwares }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loadingCurrent, setLoadingCurrent] = useState(false);

    useEffect(() => {
        if (open && user?.id) {
            setLoadingCurrent(true);
            api.get(`/softwares/user/${user.id}`)
                .then(res => setSelectedIds(res.data.map(s => s.id)))
                .catch(() => setSelectedIds([]))
                .finally(() => setLoadingCurrent(false));
        }
    }, [open, user]);

    const handleToggle = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const handleSave = async () => {
        if (!user?.id) return;
        setSaving(true);
        try {
            await api.post(`/softwares/user/${user.id}/assign`, { softwareIds: selectedIds });
            onClose(true);
        } catch { onClose(false); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle>
                <Typography fontWeight="bold">Softwares do Usuário Singular</Typography>
                <Typography variant="body2" color="text.secondary">
                    Selecione os módulos que <strong>{user?.name}</strong> poderá acessar.
                </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                {loadingCurrent ? (
                    <Box textAlign="center" sx={{ py: 4 }}><CircularProgress size={28} /></Box>
                ) : allSoftwares.length === 0 ? (
                    <Typography align="center" color="text.secondary" sx={{ py: 2 }}>Nenhum software cadastrado no sistema.</Typography>
                ) : (
                    <FormGroup>
                        {allSoftwares.map(software => (
                            <FormControlLabel
                                key={software.id}
                                control={<Checkbox checked={selectedIds.includes(software.id)} onChange={() => handleToggle(software.id)} color="primary" />}
                                label={
                                    <Box>
                                        <Typography variant="body1">{software.name}</Typography>
                                        {software.description && <Typography variant="caption" color="text.secondary">{software.description}</Typography>}
                                    </Box>
                                }
                                sx={{ mb: 1, alignItems: 'flex-start' }}
                            />
                        ))}
                    </FormGroup>
                )}
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={() => onClose(false)}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || loadingCurrent}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}>
                    {saving ? 'Salvando...' : 'Salvar Permissões'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// UserFormModal
// ---------------------------------------------------------------------------
const UserFormModal = ({ open, onClose, onSubmit, initialData, companies, units }) => {
    const isEditing = !!initialData;

    const [selectedRole, setSelectedRole] = useState('viewer');
    const [selectedLicenseType, setSelectedLicenseType] = useState('');
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [singularToggle, setSingularToggle] = useState(false);

    const isAdmin = selectedRole === 'admin';
    const isAdminWithLicense = isAdmin && selectedLicenseType !== '';
    const showCompanyFields = !singularToggle && !isAdminWithLicense;
    const availableRoles = singularToggle ? singularRoles : allRoles;

    useEffect(() => {
        if (open) {
            const initialRole = initialData?.role || 'viewer';
            const initialLicense = initialData?.licenseType || '';
            const initialCompanyId = initialData?.companyId || '';
            setSelectedRole(initialRole);
            setSelectedLicenseType(initialLicense);
            setSelectedCompanyId(initialCompanyId);
            const alreadySingular = !initialData?.companyId && !(initialRole === 'admin' && initialLicense);
            setSingularToggle(alreadySingular && !!initialData);
            const unitBelongsToCompany = units.some(u => u.id === initialData?.unitId && u.companyId === initialCompanyId);
            setSelectedUnitId(unitBelongsToCompany ? initialData?.unitId : '');
        }
    }, [open, initialData, units]);

    useEffect(() => {
        if (singularToggle) {
            const allowed = singularRoles.map(r => r.value);
            if (!allowed.includes(selectedRole)) setSelectedRole('editor');
        }
    }, [singularToggle, selectedRole]);

    useEffect(() => {
        if (!showCompanyFields) { setSelectedCompanyId(''); setSelectedUnitId(''); }
    }, [showCompanyFields]);

    useEffect(() => { if (isAdminWithLicense) setSingularToggle(false); }, [isAdminWithLicense]);

    const handleSubmitInternal = (e) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const role = formData.get('role');
        const licenseType = formData.get('licenseType');
        const isSubmitAdminWithLicense = role === 'admin' && !!licenseType;
        const isSubmitSingular = singularToggle || isSubmitAdminWithLicense;

        const userData = {
            name: formData.get('name'),
            email: formData.get('email'),
            role, cargo: formData.get('cargo'),
            companyId: isSubmitSingular ? null : (formData.get('companyId') || null),
            unitId: isSubmitSingular ? null : (formData.get('unitId') || null),
            licenseType: role === 'admin' ? (licenseType || null) : null,
            _openSoftwareModal: singularToggle,
            isSingular: singularToggle
        };
        const password = formData.get('password');
        if (password) userData.password = password;
        onSubmit(userData);
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ component: 'form', onSubmit: handleSubmitInternal, sx: { borderRadius: 3 } }} fullWidth maxWidth="sm">
            <DialogTitle fontWeight="bold">{isEditing ? 'Editar Usuário' : 'Criar Novo Usuário'}</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                <Stack spacing={2.5} sx={{ pt: 1, mt: 1, minWidth: 0 }}>
                    <TextField name="name" label="Nome Completo" fullWidth required defaultValue={initialData?.name || ''} />
                    <TextField name="email" label="Endereço de E-mail" type="email" fullWidth required defaultValue={initialData?.email || ''} InputProps={{ readOnly: isEditing }} />
                    <TextField name="cargo" label="Cargo" fullWidth required defaultValue={initialData?.cargo || ''} helperText="Ex: Gestor de Contas, Desenvolvedor" />
                    <TextField name="password" label={isEditing ? 'Nova Senha (Opcional)' : 'Senha Provisória'} type="password" fullWidth required={!isEditing}
                        helperText={isEditing ? 'Deixe em branco para não alterar.' : ''} />

                    <FormControl fullWidth>
                        <InputLabel id="role-label">Permissão</InputLabel>
                        <Select labelId="role-label" name="role" value={selectedRole} onChange={e => setSelectedRole(e.target.value)} label="Permissão">
                            {availableRoles.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {isAdmin && (
                        <FormControl fullWidth>
                            <InputLabel id="license-label">Tipo de Licença</InputLabel>
                            <Select labelId="license-label" name="licenseType" value={selectedLicenseType} onChange={e => setSelectedLicenseType(e.target.value)} label="Tipo de Licença">
                                <MenuItem value=""><em>Nenhuma (Vincular à Empresa Existente)</em></MenuItem>
                                {Object.keys(licenseConfig).map(key => (
                                    <MenuItem key={key} value={key}>{licenseConfig[key].label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {!isAdmin && (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            px: 2, py: 1.5, borderRadius: 2, border: '1px solid',
                            borderColor: singularToggle ? 'primary.main' : 'rgba(255,255,255,0.1)',
                            bgcolor: singularToggle ? alpha('#90caf9', 0.06) : 'transparent',
                            transition: 'all 0.2s', gap: 2, minWidth: 0
                        }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={500}>Usuário Singular</Typography>
                                <Typography variant="caption" color="text.secondary">Acesso individual, sem empresa cadastrada</Typography>
                            </Box>
                            <FormControlLabel control={<Switch checked={singularToggle} onChange={e => setSingularToggle(e.target.checked)} color="primary" />} label="" sx={{ m: 0 }} />
                        </Box>
                    )}

                    {showCompanyFields && (
                        <>
                            <FormControl fullWidth required>
                                <InputLabel id="company-label">Empresa</InputLabel>
                                <Select labelId="company-label" name="companyId" value={selectedCompanyId}
                                    onChange={e => { setSelectedCompanyId(e.target.value); setSelectedUnitId(''); }} label="Empresa">
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            {selectedCompanyId && (
                                <FormControl fullWidth>
                                    <InputLabel id="unit-label">Unidade</InputLabel>
                                    <Select labelId="unit-label" name="unitId" value={selectedUnitId} onChange={e => setSelectedUnitId(e.target.value)} label="Unidade">
                                        <MenuItem value=""><em>Nenhuma</em></MenuItem>
                                        {units.filter(u => u.companyId === selectedCompanyId).map(u => (
                                            <MenuItem key={u.id} value={u.id}>{u.name} {u.isMatriz ? '(Matriz)' : ''}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </>
                    )}

                    {(!showCompanyFields) && (
                        <Alert severity="info" icon={<PersonOutline fontSize="inherit" />}>
                            {isAdminWithLicense
                                ? `Este Admin receberá a licença "${licenseConfig[selectedLicenseType]?.label}". A seleção de empresa não é necessária agora.`
                                : 'Usuário singular — acesso individual sem empresa. Você definirá os softwares disponíveis no próximo passo.'}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button type="submit" variant="contained">
                    {isEditing
                        ? (singularToggle ? 'Salvar e Definir Softwares →' : 'Salvar Alterações')
                        : (singularToggle ? 'Criar e Definir Softwares →' : 'Criar Usuário')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// FilterModal (com empresa)
// ---------------------------------------------------------------------------
const FilterModal = ({ open, onClose, filters, setFilters, companies }) => {
    const [local, setLocal] = useState(filters);
    const [companySearch, setCompanySearch] = useState('');

    useEffect(() => { if (open) { setLocal(filters); setCompanySearch(''); } }, [open, filters]);

    const handleApply = () => { setFilters(local); onClose(); };
    const handleClear = () => setLocal({ role: 'all', licenseType: 'all', linkType: 'all', companyId: 'all' });

    const filteredCompanies = companies.filter(c =>
        c.name.toLowerCase().includes(companySearch.trim().toLowerCase())
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Filtros <IconButton onClick={onClose}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                <Stack spacing={3} sx={{ minWidth: 0 }}>
                    {/* Permissão */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Permissão</Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Chip label="Todas" onClick={() => setLocal(p => ({ ...p, role: 'all' }))} color="primary" variant={local.role === 'all' ? 'filled' : 'outlined'} />
                            {allRoles.map(r => (
                                <Chip key={r.value} label={r.label} onClick={() => setLocal(p => ({ ...p, role: r.value }))} color={r.color} variant={local.role === r.value ? 'filled' : 'outlined'} />
                            ))}
                        </Stack>
                    </Box>

                    {/* Licença */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Tipo de Licença</Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Chip label="Todas" onClick={() => setLocal(p => ({ ...p, licenseType: 'all' }))} color="primary" variant={local.licenseType === 'all' ? 'filled' : 'outlined'} />
                            {Object.keys(licenseConfig).map(key => (
                                <Chip key={key} label={licenseConfig[key].label} onClick={() => setLocal(p => ({ ...p, licenseType: key }))}
                                    color={licenseConfig[key].color} variant={local.licenseType === key ? 'filled' : 'outlined'} />
                            ))}
                        </Stack>
                    </Box>

                    {/* Vínculo */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Tipo de Vínculo</Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Chip label="Todos" onClick={() => setLocal(p => ({ ...p, linkType: 'all' }))} color="primary" variant={local.linkType === 'all' ? 'filled' : 'outlined'} />
                            <Chip label="Vinculado" onClick={() => setLocal(p => ({ ...p, linkType: 'company' }))} color="info" variant={local.linkType === 'company' ? 'filled' : 'outlined'} />
                            <Chip label="Singular" onClick={() => setLocal(p => ({ ...p, linkType: 'singular' }))} color="secondary" variant={local.linkType === 'singular' ? 'filled' : 'outlined'} />
                            <Chip label="Criação Pendente" onClick={() => setLocal(p => ({ ...p, linkType: 'pending' }))} color="warning" variant={local.linkType === 'pending' ? 'filled' : 'outlined'} />
                        </Stack>
                    </Box>

                    {/* Empresa */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Empresa</Typography>
                        <TextField
                            fullWidth size="small"
                            placeholder="Buscar empresa..."
                            value={companySearch}
                            onChange={(e) => setCompanySearch(e.target.value)}
                            InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
                            sx={{ mb: 1 }}
                        />
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Chip label="Todas" onClick={() => setLocal(p => ({ ...p, companyId: 'all' }))} color="primary" variant={local.companyId === 'all' ? 'filled' : 'outlined'} />
                            <Chip label="Singular" onClick={() => setLocal(p => ({ ...p, companyId: 'singular' }))} color="secondary" variant={local.companyId === 'singular' ? 'filled' : 'outlined'} />
                            <Chip label="Criação Pendente" onClick={() => setLocal(p => ({ ...p, companyId: 'pending' }))} color="warning" variant={local.companyId === 'pending' ? 'filled' : 'outlined'} />
                            {filteredCompanies.map(c => (
                                <Chip
                                    key={c.id}
                                    label={c.name}
                                    onClick={() => setLocal(p => ({ ...p, companyId: c.id }))}
                                    color="info"
                                    variant={String(local.companyId) === String(c.id) ? 'filled' : 'outlined'}
                                />
                            ))}
                            {filteredCompanies.length === 0 && (
                                <Typography variant="caption" color="text.secondary">Nenhuma empresa encontrada.</Typography>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClear}>Limpar</Button>
                <Button onClick={handleApply} variant="contained">Aplicar</Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------
const SuperadminUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [units, setUnits] = useState([]);
    const [allSoftwares, setAllSoftwares] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ type: null, data: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [softwareModal, setSoftwareModal] = useState({ open: false, user: null });
    const [csvModalOpen, setCsvModalOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [filters, setFilters] = useState({ role: 'all', licenseType: 'all', linkType: 'all', companyId: 'all' });

    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyTitle, setHistoryTitle] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [usersRes, companiesRes, unitsRes, softwaresRes] = await Promise.all([
                api.get('/superadmin/users'),
                api.get('/companies'),
                api.get('/units'),
                api.get('/softwares'),
            ]);
            const fetchedCompanies = companiesRes.data;
            const fetchedUnits = unitsRes.data;
            setCompanies(fetchedCompanies);
            setUnits(fetchedUnits);
            setAllSoftwares(softwaresRes.data);

            const companiesMap = new Map(fetchedCompanies.map(c => [c.id, c]));
            const unitsMap = new Map(fetchedUnits.map(u => [u.id, u]));

            setUsers(usersRes.data.map(user => {
                const isPendingCompany = !user.companyId && user.role === 'admin' && !!user.licenseType;
                const isManualSingular = !user.companyId && !isPendingCompany;
                return {
                    ...user,
                    company: companiesMap.get(user.companyId) || null,
                    unit: unitsMap.get(user.unitId) || null,
                    isSingular: !user.companyId,
                    isPendingCompany,
                    isManualSingular
                };
            }));
        } catch (error) {
            setSnackbar({ open: true, message: 'Não foi possível carregar os dados.', severity: 'error' });
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCloseModal = () => setModal({ type: null, data: null });
    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const handleSaveUser = async (userData) => {
        const openSoftwareModal = userData._openSoftwareModal;
        const { _openSoftwareModal, ...payload } = userData;
        try {
            let savedUser = null;
            if (modal.type === 'edit') {
                const res = await api.put(`/users/${modal.data.id}`, payload);
                savedUser = res.data?.user || res.data || modal.data;
                setSnackbar({ open: true, message: 'Usuário atualizado com sucesso!', severity: 'success' });
            } else {
                const res = await api.post('/users', payload);
                savedUser = res.data;
                setSnackbar({ open: true, message: 'Usuário criado com sucesso!', severity: 'success' });
            }
            handleCloseModal();
            fetchData();
            if (openSoftwareModal && savedUser) {
                const userId = savedUser.id || modal.data?.id;
                const userName = savedUser.name || modal.data?.name || '';
                if (userId) setSoftwareModal({ open: true, user: { id: userId, name: userName } });
            }
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Ocorreu um erro.', severity: 'error' });
            handleCloseModal();
        }
    };

    const handleDeleteUser = async () => {
        if (modal.type !== 'delete' || !modal.data) return;
        try {
            await api.delete(`/users/${modal.data.id}`);
            setSnackbar({ open: true, message: 'Usuário removido!', severity: 'warning' });
            fetchData();
        } catch (error) {
            setSnackbar({ open: true, message: 'Não foi possível remover o usuário.', severity: 'error' });
        }
        handleCloseModal();
    };

    const handleOpenSoftwareDirect = (user) => {
        setSoftwareModal({ open: true, user: { id: user.id, name: user.name } });
    };

    const handleCloseSoftwareModal = (saved) => {
        setSoftwareModal({ open: false, user: null });
        if (saved) setSnackbar({ open: true, message: 'Softwares atualizados com sucesso!', severity: 'success' });
    };

    const handleOpenGlobalHistory = async () => {
        setHistoryTitle('Visualizando acessos de todos os usuários da plataforma');
        setHistoryModalOpen(true);
        setHistoryLoading(true);
        try {
            const response = await api.get('/users/login-history');
            setHistoryLogs(response.data);
        } catch (error) {
            setSnackbar({ open: true, message: 'Erro ao carregar histórico global.', severity: 'error' });
            setHistoryLogs([]);
        } finally { setHistoryLoading(false); }
    };

    const handleOpenUserHistory = async (targetUser) => {
        setHistoryTitle(`Visualizando acessos de: ${targetUser.name}`);
        setHistoryModalOpen(true);
        setHistoryLoading(true);
        try {
            const response = await api.get(`/users/${targetUser.id}/login-history`);
            setHistoryLogs(response.data);
        } catch (error) {
            setSnackbar({ open: true, message: 'Erro ao carregar histórico do usuário.', severity: 'error' });
            setHistoryLogs([]);
        } finally { setHistoryLoading(false); }
    };

    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            const term = searchTerm.trim().toLowerCase();
            const searchMatch = !term ||
                u.name?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term) ||
                u.cargo?.toLowerCase().includes(term);

            const roleMatch = filters.role === 'all' || u.role === filters.role;
            const licenseMatch = filters.licenseType === 'all' || u.licenseType === filters.licenseType;

            const linkMatch =
                filters.linkType === 'all' ||
                (filters.linkType === 'company' && !u.isSingular) ||
                (filters.linkType === 'singular' && u.isManualSingular) ||
                (filters.linkType === 'pending' && u.isPendingCompany);

            const companyMatch =
                filters.companyId === 'all' ||
                (filters.companyId === 'singular' && u.isManualSingular) ||
                (filters.companyId === 'pending' && u.isPendingCompany) ||
                (u.companyId && String(u.companyId) === String(filters.companyId));

            return searchMatch && roleMatch && licenseMatch && linkMatch && companyMatch;
        });
    }, [users, searchTerm, filters]);

    const activeFilterCount =
        (filters.role !== 'all' ? 1 : 0) +
        (filters.licenseType !== 'all' ? 1 : 0) +
        (filters.linkType !== 'all' ? 1 : 0) +
        (filters.companyId !== 'all' ? 1 : 0);

    // ---------------------------------------------------------------------------
    // Card Mobile
    // ---------------------------------------------------------------------------
    const UserCard = ({ user }) => {
        const avatarUrl = user.avatarUrl ? new URL(`/uploads/${user.avatarUrl}`, API_BASE_URL).href : '';
        return (
            <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <DarkPaper sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ flexGrow: 1, overflow: 'hidden', minWidth: 0 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', flexShrink: 0 }} src={avatarUrl}>
                                {!user.avatarUrl && user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : ''}
                            </Avatar>
                            <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight="bold" noWrap>{user.name}</Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>{user.email}</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" sx={{ flexShrink: 0 }}>
                            <Tooltip title="Histórico de Acesso"><IconButton size="small" onClick={() => handleOpenUserHistory(user)}><History fontSize="small" /></IconButton></Tooltip>
                            {user.isManualSingular && (
                                <Tooltip title="Gerenciar Softwares">
                                    <IconButton size="small" color="info" onClick={() => handleOpenSoftwareDirect(user)}><Apps fontSize="small" /></IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Editar"><IconButton size="small" onClick={() => setModal({ type: 'edit', data: user })}><ModeEditOutline fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Remover"><IconButton size="small" onClick={() => setModal({ type: 'delete', data: user })}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                    </Stack>

                    <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.05)' }} />

                    <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="body2" color="text.secondary">Empresa</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {user.isPendingCompany && <Chip label="Criação Pendente" size="small" color="secondary" variant="outlined" />}
                                {user.isManualSingular && <Chip label="Singular" size="small" color="primary" variant="outlined" />}
                                {!user.isSingular && <Typography variant="body2" fontWeight={500} noWrap>{user.company?.name || 'N/A'}</Typography>}
                            </Box>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="body2" color="text.secondary">Unidade</Typography>
                            {user.unit ? <Chip label={`${user.unit.name} ${user.unit.isMatriz ? '(Matriz)' : ''}`} size="small" variant="outlined" color={user.unit.isMatriz ? 'primary' : 'default'} sx={{ borderColor: 'rgba(255,255,255,0.1)', maxWidth: '60%' }} /> : <Chip label="N/A" size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />}
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Permissão</Typography>
                            <Chip label={roleConfig[user.role]?.label || user.role} color={roleConfig[user.role]?.color || 'default'} size="small" />
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Licença</Typography>
                            {user.licenseType ? <Chip label={licenseConfig[user.licenseType]?.label || user.licenseType} color={licenseConfig[user.licenseType]?.color || 'default'} size="small" /> : <Chip label="N/A" size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />}
                        </Stack>
                    </Stack>
                </DarkPaper>
            </motion.div>
        );
    };

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    return (
        <ThemeProvider theme={customTheme}>
            <GlobalStyles styles={{
                'html, body': { overflowX: 'hidden', maxWidth: '100vw' },
                '*': { boxSizing: 'border-box' },
                '#root': { overflowX: 'hidden', maxWidth: '100vw' },
            }} />
            <CssBaseline />
            <Box sx={{
                width: '100%',
                maxWidth: '100vw',
                minHeight: '100vh',
                bgcolor: 'background.default',
                pb: 8,
                overflowX: 'hidden',
            }}>
                <Container
                    maxWidth={false}
                    disableGutters
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        width: '100%',
                        maxWidth: '1400px',
                        mx: 'auto',
                        minWidth: 0,
                        overflowX: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                        spacing={2}
                        sx={{ mb: 4, minWidth: 0 }}
                    >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} color="text.primary">
                                Gerenciamento Global de Usuários
                            </Typography>
                            <Typography color="text.secondary">
                                Visualize e gerencie todos os usuários da plataforma.
                            </Typography>
                        </Box>

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            sx={{ flexShrink: 0, width: { xs: '100%', lg: 'auto' }, minWidth: 0 }}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<History />}
                                onClick={handleOpenGlobalHistory}
                                sx={{ py: 1.5, px: 3, borderRadius: 2, borderColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }}
                            >
                                Logs
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<UploadFile />}
                                onClick={() => setCsvModalOpen(true)}
                                sx={{ py: 1.5, px: 3, borderRadius: 2, borderColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }}
                            >
                                Importar CSV
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setModal({ type: 'create', data: null })}
                                sx={{ py: 1.5, px: 3, borderRadius: 2, flexShrink: 0 }}
                            >
                                Criar Usuário
                            </Button>
                        </Stack>
                    </Stack>

                    {/* Busca + Filtros */}
                    <DarkPaper sx={{ p: 2, mb: 4 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
                            <TextField
                                fullWidth variant="outlined"
                                placeholder="Pesquisar por nome, email ou cargo..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{ startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>) }}
                                sx={{ minWidth: 0 }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<FilterList />}
                                onClick={() => setFilterModalOpen(true)}
                                sx={{ py: 1.5, px: 4, whiteSpace: 'nowrap', borderColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }}
                            >
                                Filtrar {activeFilterCount > 0 && `(${activeFilterCount})`}
                            </Button>
                        </Stack>

                        {activeFilterCount > 0 && (
                            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                                {filters.role !== 'all' && (
                                    <Chip
                                        size="small"
                                        label={`Permissão: ${roleConfig[filters.role]?.label}`}
                                        onDelete={() => setFilters(p => ({ ...p, role: 'all' }))}
                                        color={roleConfig[filters.role]?.color || 'default'}
                                    />
                                )}
                                {filters.licenseType !== 'all' && (
                                    <Chip
                                        size="small"
                                        label={`Licença: ${licenseConfig[filters.licenseType]?.label}`}
                                        onDelete={() => setFilters(p => ({ ...p, licenseType: 'all' }))}
                                        color={licenseConfig[filters.licenseType]?.color || 'default'}
                                    />
                                )}
                                {filters.linkType !== 'all' && (
                                    <Chip
                                        size="small"
                                        label={`Vínculo: ${filters.linkType === 'company' ? 'Vinculado' : filters.linkType === 'singular' ? 'Singular' : 'Pendente'}`}
                                        onDelete={() => setFilters(p => ({ ...p, linkType: 'all' }))}
                                        color="secondary"
                                    />
                                )}
                                {filters.companyId !== 'all' && (
                                    <Chip
                                        size="small"
                                        label={`Empresa: ${
                                            filters.companyId === 'singular' ? 'Singular' :
                                            filters.companyId === 'pending'  ? 'Criação Pendente' :
                                            companies.find(c => String(c.id) === String(filters.companyId))?.name || '—'
                                        }`}
                                        onDelete={() => setFilters(p => ({ ...p, companyId: 'all' }))}
                                        color="info"
                                    />
                                )}
                                <Chip
                                    size="small"
                                    label="Limpar tudo"
                                    variant="outlined"
                                    onClick={() => setFilters({ role: 'all', licenseType: 'all', linkType: 'all', companyId: 'all' })}
                                    sx={{ borderColor: 'rgba(255,255,255,0.1)' }}
                                />
                            </Stack>
                        )}
                    </DarkPaper>

                    {/* Lista / Tabela */}
                    {isMobile ? (
                        <Stack spacing={2}>
                            {loading ? <Box textAlign="center" sx={{ py: 10 }}><CircularProgress /></Box>
                                : filteredUsers.length === 0
                                    ? <Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>Nenhum usuário encontrado com esses critérios.</Typography>
                                    : <AnimatePresence>{filteredUsers.map(u => <UserCard key={u.id} user={u} />)}</AnimatePresence>}
                        </Stack>
                    ) : (
                        <DarkPaper sx={{ borderRadius: 4, overflow: 'hidden', width: '100%', minWidth: 0 }}>
                            <TableContainer sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
                                <Table
                                    size="small"
                                    sx={{
                                        width: '100%',
                                        tableLayout: 'fixed',
                                        '& .MuiTableCell-root': {
                                            px: { xs: 0.75, md: 1.5 },
                                            py: 1.25,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        },
                                    }}
                                >
                                    <TableHead sx={{ bgcolor: alpha('#90caf9', 0.04) }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Nome</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Empresa</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Unidade</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: '11%' }}>Permissão</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: '7%' }}>Licença</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', width: '8%', pr: 1 }}>Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                                        ) : filteredUsers.length === 0 ? (
                                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">Nenhum usuário encontrado com esses critérios.</Typography></TableCell></TableRow>
                                        ) : (
                                            <AnimatePresence>
                                                {filteredUsers.map(user => {
                                                    const avatarUrl = user.avatarUrl ? new URL(`/uploads/${user.avatarUrl}`, API_BASE_URL).href : '';
                                                    return (
                                                        <motion.tr key={user.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                                            <TableCell>
                                                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                                                                    <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', flexShrink: 0, width: 32, height: 32, fontSize: 14 }} src={avatarUrl}>
                                                                        {!user.avatarUrl && user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : ''}
                                                                    </Avatar>
                                                                    <Typography variant="body2" fontWeight="500" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {user.name}
                                                                    </Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                                                            <TableCell>
                                                                {user.isPendingCompany && <Chip label="Pendente" size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                                                                {user.isManualSingular && <Chip label="Singular" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                                                                {!user.isSingular && (user.company?.name || '—')}
                                                            </TableCell>
                                                            <TableCell>
                                                                {user.unit ? (
                                                                    <Chip
                                                                        label={user.unit.isMatriz ? `Matriz` : user.unit.name}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color={user.unit.isMatriz ? 'primary' : 'default'}
                                                                        sx={{
                                                                            borderColor: user.unit.isMatriz ? undefined : 'rgba(255,255,255,0.1)',
                                                                            maxWidth: '100%',
                                                                            height: 22,
                                                                            fontSize: 11,
                                                                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Chip label="N/A" size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)', height: 22, fontSize: 11 }} />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip label={roleConfig[user.role]?.label || user.role} color={roleConfig[user.role]?.color || 'default'} size="small" sx={{ height: 22, fontSize: 11, maxWidth: '100%' }} />
                                                            </TableCell>
                                                            <TableCell>
                                                                {user.licenseType ? (
                                                                    <Chip label={licenseConfig[user.licenseType]?.label || user.licenseType} color={licenseConfig[user.licenseType]?.color || 'default'} size="small" sx={{ height: 22, fontSize: 11 }} />
                                                                ) : (
                                                                    <Chip label="—" size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)', height: 22, fontSize: 11 }} />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ pr: 1 }}>
                                                                <Stack direction="row" spacing={0} justifyContent="flex-end">
                                                                    <Tooltip title="Histórico">
                                                                        <IconButton size="small" onClick={() => handleOpenUserHistory(user)}>
                                                                            <History fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    {user.isManualSingular && (
                                                                        <Tooltip title="Softwares">
                                                                            <IconButton size="small" color="info" onClick={() => handleOpenSoftwareDirect(user)}>
                                                                                <Apps fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    <Tooltip title="Editar">
                                                                        <IconButton size="small" onClick={() => setModal({ type: 'edit', data: user })}>
                                                                            <ModeEditOutline fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Remover">
                                                                        <IconButton size="small" onClick={() => setModal({ type: 'delete', data: user })}>
                                                                            <DeleteOutline fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </TableCell>
                                                        </motion.tr>
                                                    );
                                                })}
                                            </AnimatePresence>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DarkPaper>
                    )}
                </Container>

                <CsvImportModal
                    open={csvModalOpen}
                    onClose={() => setCsvModalOpen(false)}
                    onSuccess={fetchData}
                    setSnackbar={setSnackbar}
                    companies={companies}
                    units={units}
                    allSoftwares={allSoftwares}
                />

                <UserFormModal
                    open={modal.type === 'create' || modal.type === 'edit'}
                    onClose={handleCloseModal}
                    onSubmit={handleSaveUser}
                    initialData={modal.data}
                    companies={companies}
                    units={units}
                />

                <SingularSoftwareModal open={softwareModal.open} onClose={handleCloseSoftwareModal} user={softwareModal.user} allSoftwares={allSoftwares} />

                <FilterModal
                    open={filterModalOpen}
                    onClose={() => setFilterModalOpen(false)}
                    filters={filters}
                    setFilters={setFilters}
                    companies={companies}
                />

                <LoginHistoryModal
                    open={historyModalOpen}
                    onClose={() => setHistoryModalOpen(false)}
                    logs={historyLogs}
                    title={historyTitle}
                    loading={historyLoading}
                />

                <Dialog open={modal.type === 'delete'} onClose={handleCloseModal} PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle fontWeight="bold">Confirmar Remoção</DialogTitle>
                    <DialogContent>
                        <Typography>Tem certeza que deseja remover o usuário <Typography component="span" fontWeight="bold">{modal.data?.name}</Typography>?</Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleCloseModal}>Cancelar</Button>
                        <Button onClick={handleDeleteUser} variant="contained" color="error">Confirmar Remoção</Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">{snackbar.message}</Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default SuperadminUsersPage;